import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { ModalController, AlertController, LoadingController, Platform } from "@ionic/angular/lazy";
import { StorageService } from "../../services/storage.service";
import { ObserverService } from "../../services/observer.service";
import { StripeTerminalReaderService } from "../../services/stripe-terminal-reader.service";
import { Network } from "@capacitor/network";
import { APIService } from "src/app/services/api.service";
import { AuthService } from "src/app/services/auth.service";
import { ConnectedAccountService } from "src/app/services/connected-account.service";
import { Router } from "@angular/router";

@Component({
    selector: 'app-settings-change-stripe-terminal-reader',
    templateUrl: './settings-change-stripe-terminal-reader.page.html',
    styleUrls: ["./settings-change-stripe-terminal-reader.page.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SettingsChangeStripeTerminalReaderPage implements OnInit {
    
	readers = [];
	selected_reader = null;
	online = false;
	loading_readers = true;
	reader_connection_status_colors = { online: "var(--ion-color-success)", offline: "var(--ion-color-danger)" };
	saved_reader = null;
    stripe_connected = false;
    settings = null;

	constructor(
        private router: Router,
		public modalController: ModalController,
		public alertController: AlertController,
		public loadingController: LoadingController,
		private storageService: StorageService,
		private observerService: ObserverService,
        private apiService: APIService,
        private authService: AuthService,
        private connectedAccountService: ConnectedAccountService,
		public readerService: StripeTerminalReaderService,
		private platform: Platform
	) {}

	async ngOnInit() {

		this.platform.ready().then(async () => {
			let network_status = await Network.getStatus();
			if(network_status.connected) {
				this.online = true;
                const loading = this.loadingController.create({ message: "Loading readers" });
                loading.then(loaded => loaded.present());        
                this.loadSettings().then(() => {
                    if(this.settings.stripe_connected === "true") {
                        this.stripe_connected = true;
                        this.loadReaders();
                    }
                }).finally(() => {
                    this.loading_readers = false;
                    loading.then(loaded => loaded.dismiss());
                });
			}
		});

		this.selected_reader = await this.readerService.getConnectedReader();

		this.observerService.subscribe("stripe_terminal_reader_forgotten|setting_change_stripe_terminal_reader_modal", reader => {
			this.saved_reader = null;
		});

		this.observerService.subscribe("stripe_terminal_reader_changed|setting_change_stripe_terminal_reader_modal", reader => {
			this.selected_reader = reader;
			if (reader !== null) {
				this.saved_reader = reader;
			}
		});

        this.observerService.subscribe('network_status_changed|setting_change_stripe_terminal_reader_modal', (network_status) => {
            if(network_status.connected) {
                this.online = network_status.connected;
            }
        });

		this.storageService.get("stripe_terminal_reader").then(reader => (this.saved_reader = reader));

	}

    ngOnDestroy() {
        this.observerService.destroy("stripe_terminal_reader_forgotten|setting_change_stripe_terminal_reader_modal");
        this.observerService.destroy("stripe_terminal_reader_changed|setting_change_stripe_terminal_reader_modal");
        this.observerService.destroy("network_status_changed|setting_change_stripe_terminal_reader_modal");
    }

	loadReaders() {
        if(!this.stripe_connected) return;
		this.loading_readers = true;
		this.readers = [];
		this.readerService
			.loadReaders()
			.then(({readers,error}) => {
                if(error){
                    if(error === 'stripe_not_connected'){
                        this.stripe_connected = false;
                    }
                }else{
                    this.stripe_connected = true;
                    this.readers = readers.filter(reader => ["bbpos_wisepos_e", "stripe_s700", "stripe_s710"].includes(reader.device_type));
                }
			})
			.finally(() => {
                this.loading_readers = false;
            });
	}

    loadSettings() {
        return new Promise<void>((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.storageService.get("connected_account_id").then(connected_account_id => {
					if (connected_account_id) {
						this.connectedAccountService.getConnectedAccount(connected_account_id).then(connected_account => {
                            if (connected_account) {
                                this.apiService.getSettings(connected_account).then((settings: {}) => {
                                    if(settings) {
                                        this.settings = settings;
                                    }
                                    resolve();
                                })
                                .catch((error) => {
                                    if(error.status === 400) {
                                        this.presentAlert('Unknown error', "Unfortunately something went wrong when trying to retrieve the settings.");
                                    }
                                    else if(error.status == 403) {
                                        this.presentAlert('Insufficient permissions', "You don't have permission to view settings.");
                                    }
                                    else if(error.status == 404) {
                                        this.presentAlert('Unavailable', "Unfortunately we're unable to retrieve settings at this time.");
                                    }
                                    else if(error.status == 0) {
                                        this.presentAlert("Connection failure", "Unable to reach the server. Please try again.");
                                    }
                                    reject();
                                });
                            } 
                            else {
                                reject();
                            }
                        })
                        .catch(error => {
                            reject(error);
                        });
					} 
                    else {
						reject();
					}
				})
				.catch(error => {
					reject(error);
				});                
            })
            .catch(() => {
                this.router.navigate(['switch-account'], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

	disconnect() {
		this.readerService.disconnectReader();
		this.selected_reader = null;
	}

	forgetReader() {
		if (this.selected_reader) this.readerService.disconnectReader();
		this.readerService.forgetReader();
	}

	capitalise(x: string) {
		return x[0].toUpperCase() + x.substring(1);
	}

	close(): void {
		this.modalController.dismiss(this.selected_reader ? "connected" : "not_connected");
	}

	async update(reader) {
		const reader_res = await this.readerService.connectReaderWithLoading(reader);
		if (reader_res === "connected") {
			this.selected_reader = reader;
			this.storageService.set("stripe_terminal_reader", this.selected_reader).then(() => {
				this.observerService.publish("stripe_terminal_reader_changed", this.selected_reader);
			});
			this.modalController.dismiss("connected");
		} else {
			this.presentAlert("Unavailable", `Couldn't connect to reader ${reader.label}`);
		}
	}

	async presentAlert(header, message) {
		const alert = await this.alertController.create({
			header: header,
			message: message,
			buttons: ["OK"],
		});
		await alert.present();
	}
}
